import React, { useState } from 'react';

import { ROUTES } from '@elara/client-core/constants';

import { useSessionState } from '#states/hooks.js';
import { Alert, Button, FloatingInput, IconSizes, MaterialIcon } from '#ui/index.js';
import { SessionLayout } from '#views/layout/SessionLayout.jsx';
import { Link, LinkButton } from '#views/Router.jsx';

export function ForgotPasswordPage() {
  const sessionState = useSessionState();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setIsSubmitting(true);
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const actionCodeSettings = {
        // `url` becomes the `continueUrl` query parameter on the action handler.
        // We still redirect users to login with a success banner after completing the reset.
        url: `${origin}${ROUTES.LOGIN}?passwordReset=1`,
        handleCodeInApp: true,
      };
      await sessionState.sendPasswordResetEmail(email.trim(), actionCodeSettings);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footerContent = (
    <p>
      Remember your password?{' '}
      <Link to={ROUTES.LOGIN} className="text-accent hover:underline bg-transparent border-none p-0 m-0 cursor-pointer">
        Sign in
      </Link>
    </p>
  );

  return (
    <SessionLayout title={success ? 'Check your inbox' : 'Reset your password'} footer={footerContent}>
      {success ? (
        <div className="space-y-6">
          <p className="text-pretty">
            We sent an email to <span className="font-semibold">{email}</span>. Click the link in the email to set a new
            password.
          </p>
          <Alert className="flex items-center gap-2">
            <MaterialIcon icon="info" size={IconSizes.xs} />
            <span>If you don’t see the email in your inbox, check your spam folder.</span>
          </Alert>
          <div className="flex flex-col">
            <LinkButton to={ROUTES.LOGIN} size="lg">
              Back to sign in
            </LinkButton>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <Alert variant="destructive">{error}</Alert>}
          <p className="text-pretty">
            Enter the email address you use to sign in and we&apos;ll send you a link to reset your password.
          </p>
          <FloatingInput
            id="forgot-email"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
            autoComplete="email"
          />
          <div className="flex flex-col">
            <Button type="submit" size="lg" disabled={isSubmitting}>
              Send reset link
            </Button>
          </div>
        </form>
      )}
    </SessionLayout>
  );
}
